import { useState } from 'react';
import { MapPin, Truck, Check, X, Loader2 } from 'lucide-react';

// Gujarat pincodes - major cities and towns
const GUJARAT_PINCODES = new Set([
  // Kutch District (Home base - faster delivery)
  '370001', '370002', '370003', '370004', '370005', '370006', '370007', '370015', '370020', '370030',
  '370040', '370105', '370110', '370115', '370130', '370140', '370150', '370155', '370160', '370165',
  '370201', '370205', '370210', '370230', '370240', '370405', '370410', '370415', '370421', '370425',
  '370427', '370430', '370435', '370445', '370450', '370455', '370460', '370465', '370475', '370485',
  '370490', '370510', '370601', '370605', '370610', '370615', '370620', '370625', '370627', '370630',
  '370640', '370650', '370655', '370660', '370665', '370670', '370675',
  // Bhuj specific
  '370001', '370020', '370030',
  // Anjar specific  
  '370110',
  // Ahmedabad
  '380001', '380002', '380003', '380004', '380005', '380006', '380007', '380008', '380009', '380010',
  '380013', '380014', '380015', '380016', '380018', '380019', '380021', '380022', '380023', '380024',
  '380026', '380027', '380028', '380050', '380051', '380052', '380054', '380055', '380058', '380059',
  '380060', '380061', '380063', '382210', '382330', '382340', '382345', '382350', '382355',
  // Surat
  '394101', '394102', '394103', '394104', '394105', '394107', '394110', '394111', '394115', '394120',
  '394125', '394130', '394150', '394155', '394160', '394163', '394170', '394180', '394185', '394190',
  '395001', '395002', '395003', '395004', '395005', '395006', '395007', '395008', '395009', '395010',
  // Vadodara
  '390001', '390002', '390003', '390004', '390005', '390006', '390007', '390008', '390009', '390010',
  '390011', '390012', '390013', '390014', '390015', '390016', '390017', '390018', '390019', '390020',
  // Rajkot
  '360001', '360002', '360003', '360004', '360005', '360006', '360007', '360020', '360021', '360022',
  '360023', '360024', '360025', '360026', '360027', '360035', '360050', '360055', '360060', '360070',
  // Jamnagar
  '361001', '361002', '361003', '361004', '361005', '361006', '361007', '361008', '361009', '361010',
  // Bhavnagar
  '364001', '364002', '364003', '364004', '364005', '364006',
  // Gandhinagar
  '382010', '382016', '382020', '382021', '382024', '382026', '382028', '382030', '382041', '382042',
  // Junagadh
  '362001', '362002', '362003', '362004', '362011', '362015', '362020', '362037', '362130', '362140',
  // Other Gujarat cities
  '388001', '387001', '387002', '396001', '396193', '384001', '384002', '385001', '385535',
]);

// Kutch pincodes for faster delivery
const KUTCH_PINCODES = new Set([
  '370001', '370002', '370003', '370004', '370005', '370006', '370007', '370015', '370020', '370030',
  '370040', '370105', '370110', '370115', '370130', '370140', '370150', '370155', '370160', '370165',
  '370201', '370205', '370210', '370230', '370240', '370405', '370410', '370415', '370421', '370425',
  '370427', '370430', '370435', '370445', '370450', '370455', '370460', '370465', '370475', '370485',
  '370490', '370510', '370601', '370605', '370610', '370615', '370620', '370625', '370627', '370630',
  '370640', '370650', '370655', '370660', '370665', '370670', '370675',
]);

const checkDelivery = (pincode) => {
  // Validate pincode format
  if (!/^\d{6}$/.test(pincode)) {
    return { available: false, error: 'Please enter a valid 6-digit pincode' };
  }

  // Kutch district - fastest delivery (1-2 days)
  if (KUTCH_PINCODES.has(pincode)) {
    return {
      available: true,
      days: '1-2',
      location: 'Kutch District',
      message: 'Express delivery available!'
    };
  }

  // Other Gujarat - fast delivery (2-3 days)
  if (GUJARAT_PINCODES.has(pincode)) {
    return {
      available: true,
      days: '2-3',
      location: 'Gujarat',
      message: 'Free delivery on orders above ₹999'
    };
  }

  // Rest of India - standard delivery (4-6 days)
  // Indian pincodes start with 1-9
  if (pincode[0] >= '1' && pincode[0] <= '9') {
    return {
      available: true,
      days: '4-6',
      location: 'Pan India',
      message: 'Standard delivery across India'
    };
  }

  return { available: false, error: 'Delivery not available for this pincode' };
};

export const PincodeChecker = ({ className = '' }) => {
  const [pincode, setPincode] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCheck = async () => {
    if (!pincode) return;
    
    setLoading(true);
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const deliveryResult = checkDelivery(pincode);
    setResult(deliveryResult);
    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCheck();
    }
  };

  return (
    <div className={`${className}`} data-testid="pincode-checker">
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-4 h-4 text-[#C59746]" />
        <span className="text-sm font-semibold text-[#1A1A1A]">Check Delivery</span>
      </div>
      
      <div className="flex gap-2">
        <input
          type="text"
          value={pincode}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
            setPincode(value);
            if (value.length < 6) setResult(null);
          }}
          onKeyPress={handleKeyPress}
          placeholder="Enter pincode"
          maxLength={6}
          className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C59746] focus:ring-1 focus:ring-[#C59746]"
          data-testid="pincode-input"
        />
        <button
          onClick={handleCheck}
          disabled={pincode.length !== 6 || loading}
          className="px-4 py-2.5 bg-[#C59746] text-white rounded-lg text-sm font-semibold hover:bg-[#B8860B] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          data-testid="check-pincode-button"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'CHECK'}
        </button>
      </div>

      {/* Result */}
      {result && (
        <div className={`mt-3 p-3 rounded-lg ${result.available ? 'bg-green-50' : 'bg-red-50'}`} data-testid="pincode-result">
          {result.available ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-sm font-semibold text-green-700">Delivery Available</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Truck className="w-4 h-4" />
                <span>Estimated delivery in <strong>{result.days} business days</strong></span>
              </div>
              {result.message && (
                <p className="text-xs text-green-600">{result.message}</p>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <X className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">{result.error}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PincodeChecker;
